import { Button, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { JobProgress } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../api/operationsApi";
import { useExecutionState } from "../../../hooks/useExecutionState";
import { isFormValid } from "../../../utilities/forms";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import OrganizationCompanySelector from "../../common/OrganizationCompanySelector";
import OrganizationSelector from "../../common/OrganizationSelector";
import PageTitle from "../../common/PageTitle";
import ConfirmDeprovisionDialog from "./ConfirmDeprovisionDialog";
import {
  getInitialFundManagerDeprovisioningForm,
  mapFundManagerDeprovisioningFormToRequest,
  validateFundManagerDeprovisioningForm,
} from "./fundManagerDeprovisioningForm";

const deprovisionFundManagerClient = withErrorHandling(api.clientDeprovisioning.deprovisionFundManagerClient);
const cancelDeprovisionJob = withErrorHandling(api.clientDeprovisioning.cancelJob);

const FundManagerDeprovisioning = () => {
  const [form, setForm] = useState(getInitialFundManagerDeprovisioningForm);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [operationId, setOperationId] = useState<string>();
  const [isCancelling, setIsCancelling] = useState(false);
  const { executionState, setExecutionState } = useExecutionState();

  const [organizations, fetchOrganizationsError, { isFetching, fetch: refetchOrganizations }] = useFetch(
    api.clientProvisioning.getExistingOrganizations
  );

  const handleJobFinished = useCallback(
    (jobProgress: JobProgress) => {
      setOperationId(undefined);
      setIsCancelling(false);

      if (jobProgress.properties["Cancelled"] === "true") {
        setExecutionState({
          status: "warning",
          text: "Deprovisioning was cancelled.",
          details: ["The client may be partially deleted. Review it before retrying."],
        });
        refetchOrganizations();
        setForm(getInitialFundManagerDeprovisioningForm());
        return;
      }

      if (jobProgress.isSuccess) {
        setExecutionState({
          status: "success",
          details: ["Client deprovisioning completed successfully."],
        });
        refetchOrganizations();
        setForm(getInitialFundManagerDeprovisioningForm());
      } else {
        setExecutionState({
          status: "error",
          errorMessage: jobProgress.errorMessage,
        });
      }
    },
    [setExecutionState, refetchOrganizations, setForm]
  );

  const { jobProgress, startJobProgressCheck } = useJobProgress(api.clientDeprovisioning.getJobProgress, {
    onJobFinished: handleJobFinished,
  });

  if (fetchOrganizationsError) {
    return <LoadingFailedAlert apiError={fetchOrganizationsError} />;
  }

  if (organizations === undefined || isFetching) {
    return <InlineLoader />;
  }

  const handleDeprovision = async () => {
    setShowConfirmDialog(false);

    const validationResults = validateFundManagerDeprovisioningForm(form);
    setForm({ ...form, validationResults });
    if (!isFormValid(validationResults)) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const payload = mapFundManagerDeprovisioningFormToRequest(form);
    const [resp, error] = await deprovisionFundManagerClient(payload);
    if (error) {
      setExecutionState({ status: "error", error });
      return;
    }

    setOperationId(resp.operationId);
    startJobProgressCheck(resp.operationId);
  };

  const handleCancel = async () => {
    if (!operationId) {
      return;
    }

    setIsCancelling(true);
    const [, error] = await cancelDeprovisionJob(operationId);
    if (error) {
      logError(error, "[FundManagerDeprovisioning] cancelJob");
      setExecutionState({ status: "error", error });
      setIsCancelling(false);
    }
  };

  const isProcessing = executionState.status === "in-progress" || jobProgress !== undefined;
  const canDeprovision = form.organization !== null && form.client !== null && !isProcessing;

  // Get managed clients from selected organization
  const managedClients = form.organization?.managesClients || [];

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle
        title="Deprovision Fund Manager Client"
        subtitle="📋 What gets deleted (25 operations)"
        defaultExpanded={false}
      >
        ⚠️ <strong>WARNING:</strong> This operation will permanently delete the client and all its data
        <br />
        <br />
        1. Remove API Gateway subscriptions
        <br />
        2. Remove client branding files from blob storage
        <br />
        3. Remove client dictionaries from Cosmos DB
        <br />
        4. Remove client activity logs
        <br />
        5. Remove client contacts from Cosmos DB (optional)
        <br />
        6. Remove client investors from Cosmos DB (optional)
        <br />
        7. Remove client funds from Cosmos DB (optional)
        <br />
        8. Remove extended client data from Core, DwhManager, and IrEngine (optional - approvals, change requests, data
        imports, inbox items, dashboards, widgets, webhooks, document views, document event sessions, user document
        views, etc.)
        <br />
        9. Clean up reporting data (optional - reports, templates, measures, dimensions)
        <br />
        10. Clean up users, invitations and consent agreements
        <br />
        11. Remove AI Assistant chats, their files and Foundry conversations
        <br />
        12. Remove DataCatalogue items (files, folders, tags)
        <br />
        13. Remove client from data model configurations
        <br />
        14. Disable Event Hub connection sync (BC Connector)
        <br />
        15. Disable DWH connection sync
        <br />
        16. Disable BC Event Hub sync for DWH
        <br />
        17. Remove client from managing organizations (ManagesClients and Teams app)
        <br />
        18. Drop DWH databases (SQL and Snowflake)
        <br />
        19. Drop Cosmos DB database
        <br />
        20. Delete DWH schema record
        <br />
        21. Revoke Business Central permission sets of the client&apos;s company from all BC users
        <br />
        22. Delete client record from database
        <br />
        23. Clear system caches
        <br />
        24. Delete BC Connector scheduled jobs
        <br />
        25. Refresh BC event handlers
        <br />
        <br />
        ⏱️ <strong>Duration:</strong> Deprovisioning process takes a couple of minutes
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <OrganizationSelector
          organizations={organizations}
          value={form.organization}
          onChange={(value) => setForm({ ...getInitialFundManagerDeprovisioningForm(), organization: value })}
          validationResult={form.validationResults.organization}
        />
        <FieldHelp>
          Select the parent Fund Administrator organization.
          <br />
          <br />
          Only Fund Managers managed by this organization will be available for deprovisioning.
        </FieldHelp>
      </Stack>
      {form.organization && (
        <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
          <OrganizationCompanySelector
            companies={managedClients}
            value={form.client}
            onChange={(value) => setForm({ ...form, client: value })}
            validationResult={form.validationResults.client}
          />
          <FieldHelp>
            Select the Fund Manager client to deprovision.
            <br />
            <br />
            <strong>⚠️ Verify carefully:</strong> This will permanently delete the client and all its data.
          </FieldHelp>
        </Stack>
      )}
      {form.client && (
        <>
          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.deleteClientData}
                  onChange={(e) => setForm({ ...form, deleteClientData: e.target.checked })}
                  color="error"
                />
              }
              label="Delete all client core data (contacts, investors, funds) from Cosmos DB"
            />
            <FieldHelp>
              ⚠️ <strong>Recommended:</strong> Core data cleanup.
              <br />
              Includes Contacts, Investors, Funds.
            </FieldHelp>
          </Stack>
          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.deleteOtherData}
                  onChange={(e) => setForm({ ...form, deleteOtherData: e.target.checked })}
                  color="error"
                />
              }
              label="Delete extended operational data (approvals, change requests, data collection, imports...)"
            />
            <FieldHelp>
              Deletes Approvals (v1 & v2), Approval Configurations, Change Requests, Data Collection Requests /
              Templates / Submissions, Data Imports, Data Import File Containers. More categories may be added in
              future.
              <br />
              <br />
              Uncheck if you need these for audit or post-migration analysis.
            </FieldHelp>
          </Stack>
        </>
      )}
      <Button variant="contained" color="error" onClick={() => setShowConfirmDialog(true)} disabled={!canDeprovision}>
        Deprovision Client
      </Button>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
      {jobProgress && (
        <AsyncJobProgress
          title="Deprovisioning in progress..."
          jobProgress={jobProgress}
          isCancelling={isCancelling}
          onCancel={handleCancel}
        />
      )}
      {showConfirmDialog && form.client && (
        <ConfirmDeprovisionDialog
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleDeprovision}
          clientCode={form.client.clientCode}
          clientName={form.client.name}
        />
      )}
    </Stack>
  );
};

export default FundManagerDeprovisioning;
